import './setup.css';

import { CLASSES, hpAt, ppAt } from '../core/classes.ts';
import { FACTIONS, TEAM_SIZE, type ClassId, type Faction } from '../core/constants.ts';
import { defaultLoadout } from '../core/spells.ts';
import { buildGrimoire } from './grimoire.ts';

/**
 * The muster. Everything the simulation needs to know before it can start.
 *
 * Class and loadout are both settled here and never change again — the same
 * rule for both, because "you are what you brought" is easier to read across a
 * roster than a mage who was a Magician at the last fight and is something else
 * now. Bots are per faction rather than a single number, so 5v5v0 and 2v2v2 are
 * both things you can simply ask for.
 */
export interface SetupChoice {
  name: string;
  faction: Faction;
  cls: ClassId;
  loadout: string[];
  bots: Record<Faction, number>;
  difficulty: BotDifficulty;
}

export type BotDifficulty = 'easy' | 'normal' | 'hard';

const DIFFICULTY_NOTE: Record<BotDifficulty, string> = {
  easy: 'Slow to notice you, and their aim wanders.',
  normal: 'Good but not inhuman. They hesitate, and can miss.',
  hard: 'Quick, accurate, and they lead their shots.',
};

const CLASS_ORDER: readonly ClassId[] = ['magician', 'cleric', 'mentalist', 'arcanist'];

const FACTION_MOTTO: Record<Faction, string> = {
  order: 'Champions of justice and the supremacy of The Law.',
  balance: 'Light cannot exist without the Dark.',
  chaos: 'Individuality and survival of the fittest.',
};

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K, cls?: string, text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined) node.textContent = text;
  return node;
};

export function runSetup(root: HTMLElement): Promise<SetupChoice> {
  return new Promise((resolve) => {
    const choice: SetupChoice = {
      name: 'Mage',
      faction: 'order',
      cls: 'magician',
      loadout: defaultLoadout('magician'),
      bots: { order: 0, balance: 0, chaos: 0 },
      difficulty: 'normal',
    };

    root.hidden = false;
    root.innerHTML = '';

    const sheet = el('div', 'setup-sheet');
    root.appendChild(sheet);

    // ── Screen one: the muster ───────────────────────────────────────────────
    function renderMuster(): void {
      sheet.innerHTML = '';
      sheet.appendChild(el('div', 'setup-eyebrow', 'The Cathedral'));
      sheet.appendChild(el('h1', 'setup-title', 'Take the field'));

      const nameRow = el('label', 'setup-name');
      nameRow.appendChild(el('span', 'setup-label', 'Known as'));
      const nameInput = el('input', 'setup-name-input');
      nameInput.type = 'text';
      nameInput.value = choice.name;
      nameInput.maxLength = 18;
      nameInput.spellcheck = false;
      nameInput.addEventListener('input', () => {
        choice.name = nameInput.value.trim() || 'Mage';
      });
      nameRow.appendChild(nameInput);
      sheet.appendChild(nameRow);

      // Three factions, three panels. A cathedral has a triptych; so does this.
      sheet.appendChild(el('div', 'setup-label setup-section', 'Faction'));
      const triptych = el('div', 'setup-triptych');
      for (const f of FACTIONS) {
        const panel = el('button', 'faction-panel');
        panel.type = 'button';
        panel.dataset.faction = f;
        panel.dataset.on = String(choice.faction === f);
        panel.appendChild(el('span', 'faction-name', f));
        panel.appendChild(el('span', 'faction-motto', FACTION_MOTTO[f]));
        panel.addEventListener('click', () => {
          choice.faction = f;
          renderMuster();
        });
        triptych.appendChild(panel);
      }
      sheet.appendChild(triptych);

      sheet.appendChild(el('div', 'setup-label setup-section', 'Discipline'));
      const classes = el('div', 'setup-classes');
      for (const id of CLASS_ORDER) {
        const def = CLASSES[id];
        const card = el('button', 'class-card');
        card.type = 'button';
        card.dataset.on = String(choice.cls === id);
        card.appendChild(el('span', 'class-name', def.name));

        const stats = el('span', 'class-stats');
        stats.appendChild(el('span', '', `${hpAt(id, 1)}–${hpAt(id, 30)} h`));
        stats.appendChild(el('span', '', `${ppAt(id, 1)}–${ppAt(id, 30)} p`));
        card.appendChild(stats);

        const marks: string[] = [];
        if (def.canFly) marks.push('flies');
        if (def.canConquerShrines) marks.push('takes shrines');
        if (def.canBiasPools) marks.push('holds pools');
        card.appendChild(el('span', 'class-marks', marks.join(' · ')));
        card.appendChild(el('span', 'class-blurb', def.blurb));

        card.addEventListener('click', () => {
          choice.cls = id;
          // A loadout belongs to a discipline. Changing one discards the other.
          choice.loadout = defaultLoadout(id);
          renderMuster();
        });
        classes.appendChild(card);
      }
      sheet.appendChild(classes);

      // Bots per faction, so the shape of the match is something you state
      // rather than something you end up with.
      sheet.appendChild(el('div', 'setup-label setup-section', 'Fill the ranks'));
      sheet.appendChild(el(
        'p', 'setup-note',
        'Bots fight, take objectives and level up over the match, just like a human.',
      ));

      const skill = el('div', 'setup-skill');
      for (const level of ['easy', 'normal', 'hard'] as BotDifficulty[]) {
        const pick = el('button', 'skill-option');
        pick.type = 'button';
        pick.dataset.on = String(choice.difficulty === level);
        pick.appendChild(el('span', 'skill-name', level));
        pick.appendChild(el('span', 'skill-note', DIFFICULTY_NOTE[level]));
        pick.addEventListener('click', () => {
          choice.difficulty = level;
          renderMuster();
        });
        skill.appendChild(pick);
      }
      sheet.appendChild(skill);

      const ranks = el('div', 'setup-ranks');
      for (const f of FACTIONS) {
        const row = el('div', 'rank-row');
        row.dataset.faction = f;
        row.appendChild(el('span', 'rank-name', f));

        const stepper = el('div', 'rank-stepper');
        const less = el('button', 'rank-step', '−');
        less.type = 'button';
        const count = el('span', 'rank-count', String(choice.bots[f]));
        const more = el('button', 'rank-step', '+');
        more.type = 'button';

        const nudge = (by: number) => {
          /*
           * A side holds eight mages, and the player is only one of them if they
           * are actually on that side.
           *
           * The cap was a flat seven everywhere, which is right for the player's
           * own team and wrong for the other two — a Chaos side with no human on
           * it could never be more than seven, so a solo player fighting two
           * full teams was fighting fourteen mages rather than sixteen and every
           * roster in the game was quietly a mage short.
           */
          const seats = TEAM_SIZE - (choice.faction === f ? 1 : 0);
          choice.bots[f] = Math.max(0, Math.min(seats, choice.bots[f] + by));
          count.textContent = String(choice.bots[f]);
          updateTally();
        };
        less.addEventListener('click', () => nudge(-1));
        more.addEventListener('click', () => nudge(1));

        stepper.append(less, count, more);
        row.appendChild(stepper);
        ranks.appendChild(row);
      }
      sheet.appendChild(ranks);

      // The signature: the match reads its own shape back at you.
      const tally = el('div', 'setup-tally');
      sheet.appendChild(tally);
      function updateTally(): void {
        tally.innerHTML = '';
        FACTIONS.forEach((f, i) => {
          const n = choice.bots[f] + (choice.faction === f ? 1 : 0);
          const num = el('span', 'tally-n', String(n));
          num.dataset.faction = f;
          num.dataset.empty = String(n === 0);
          tally.appendChild(num);
          if (i < FACTIONS.length - 1) tally.appendChild(el('span', 'tally-v', 'v'));
        });
      }
      updateTally();

      const actions = el('div', 'setup-actions');
      const go = el('button', 'setup-go', 'Choose your spells');
      go.type = 'button';
      go.addEventListener('click', renderGrimoire);
      actions.appendChild(go);
      sheet.appendChild(actions);
    }

    // ── Screen two: the grimoire ─────────────────────────────────────────────
    function renderGrimoire(): void {
      sheet.innerHTML = '';
      sheet.appendChild(el('div', 'setup-eyebrow', CLASSES[choice.cls].name));
      sheet.appendChild(el('h1', 'setup-title', 'Choose your spells'));
      sheet.appendChild(el(
        'p', 'setup-note',
        'Six spell slots. Each levels up through five tiers as you level; '
        + 'the range shown is tier I to tier V.',
      ));

      /*
       * The shared grimoire. It was written here first and the lobby needs the
       * same thing — and there is no version of "the same thing" that survives
       * being written twice, because two panels this dense drift within a week
       * and the drift is invisible until somebody compares them.
       */
      const index = buildGrimoire(choice.cls, choice.loadout, (slot, id) => {
        choice.loadout[slot] = id;
        renderGrimoire();
      });
      sheet.appendChild(index);

      const actions = el('div', 'setup-actions');
      const back = el('button', 'setup-back', 'Back to the muster');
      back.type = 'button';
      back.addEventListener('click', renderMuster);

      const go = el('button', 'setup-go', 'Take the field');
      go.type = 'button';
      go.addEventListener('click', () => {
        root.hidden = true;
        root.innerHTML = '';
        resolve(choice);
      });

      actions.append(back, go);
      sheet.appendChild(actions);
    }

    renderMuster();
  });
}
